<template>
  <div class="masthead-input">
    <el-input
      ref="inputRef"
      v-model="internalValue"
      :disabled="isDisabled"
      :placeholder="placeholder"
      :maxlength="maxlength"
      type="textarea"
      size="large"
      autosize
      @keydown.enter.prevent="submitOnEnter"
      @focus="handleFocus"
      @blur="submit"
    />
    <!-- Variables -->
    <dropdown 
      v-if="hasVariables" 
      :force-visibility="showVariableDropdown" 
      class="insert-variable-dropdown"
      placement="bottom-end"
    >
      <el-button class="insert-variable-button">
        Insert Variable
        <i class="ddi ddi-plus-thick" />
      </el-button>
      <template #content>
        <div>
          <ul>
            <li
              v-for="(item, index) in variables"
              :key="index"
              :data-clipboard-text="item.name"
              @click="handleVariable(item)"
            >{{ item.description }}</li>
          </ul>
        </div>
      </template>
    </dropdown>
  </div>
</template>
<script lang="ts">
/* General */
import { INameAndDescription } from '@/api/types/response.types'
import { defineComponent } from 'vue'
import { InputInstance } from 'element-plus'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */

/* API */

export default defineComponent({
  name: 'MastheadInput',

  props: {
    /**
     * Text added on input
     */
    modelValue: {
      type: [String, Number, Object, Array],
      default: '',
    },
    /**
     * Maxlength of input
     */
    maxlength: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Text to be displayed on placeholder
     */
    placeholder: {
      type: String,
      required: true,
    },
    /**
     * Is input focused
     */
    isFocused: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    selectAllOnFocus: {
      type: Boolean,
      default: false,
    },
    variables: {
      type: Array as () => INameAndDescription[],
      required: false,
      default: (): INameAndDescription[] => [],
    },
  },

  emits: ['blur', 'focus', 'input-submitted', 'update:modelValue'],

  data() {
    return {
      showVariableDropdown: false,
    }
  },

  computed: {
    internalValue: {
      get(): any {
        return this.modelValue
      },
      set(value: any): any {
        this.$emit('update:modelValue', value)
      },
    },
    hasVariables(): boolean {
      return this.variables.length > 0
    },
  },

  watch: {
    isFocused: {
      handler(newValue) {
        if (this.$refs.inputRef !== undefined && newValue) {
          // @ts-ignore
          this.$refs.inputRef.focus()
        }
      },
    },
  },

  mounted() {
    if (this.isFocused) {
      // @ts-ignore
      this.$refs.inputRef.focus()
    }
  },

  methods: {
    handleFocus() {
      if (this.selectAllOnFocus) {
        ;(this.$refs.inputRef as InputInstance).select()
      }
      this.$emit('focus')
    },

    submit() {
      this.$emit('blur')
      this.$emit('input-submitted', this.internalValue)
      // Unset when this runs from the blur the browser fires as the focused input is removed: Vue 3 clears refs before
      // removing elements
      ;(this.$refs.inputRef as InputInstance | null)?.blur()
    },

    /**
     * Enter leaves the field and the blur submits, so a save happens once. Element UI's input didn't emit keydown, so
     * a keydown listener on the root used to submit here, and the blur submitted again; Element Plus's does, which
     * made it three.
     */
    submitOnEnter() {
      ;(this.$refs.inputRef as InputInstance | null)?.blur()
    },

    async handleVariable(nad: INameAndDescription) {
      this.internalValue = this.internalValue + nad.name
      this.submit()
      this.showVariableDropdown = true
      this.$nextTick(() => {
        this.showVariableDropdown = false
      })
    },
  },
})
</script>
<style lang="scss">
.masthead-input {
  display: flex;

  .el-textarea {
    &__inner {
      padding-left: 0;
      background: transparent;
      border: transparent !important;
      box-shadow: none !important;
      overflow: hidden;
      resize: none;
      @include lettering--h1;

      &:focus {
        margin-left: 0;
        border: solid 0 transparent !important;
        box-shadow: none;
      }
    }
  }

  .el-textarea.is-disabled .el-textarea__inner {
    background: transparent;
    color: $--color-text-primary;
  }
}
</style>
